import { ChevronLeft, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from './action';

export function ForgotPasswordForm() {
  const { toggleForm } = useAuth();
  const [email, setEmail] = useState('');
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [userInput, setUserInput] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Hàm tạo captcha ngẫu nhiên
  function generateCaptcha() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Loại bỏ O, 0, I, 1
    let captchaCode = '';
    for (let i = 0; i < 6; i++) {
      captchaCode += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return captchaCode;
  }

  // Vẽ captcha lên canvas
  function drawCaptcha(code: string) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Nền captcha phẳng sạch sẽ và hiện đại
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.font = 'bold 20px sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    // Bảng màu hiện đại (Blue/Navy #023e8a, Indigo, Slate, Purple, Cyan)
    const colors = ['#023e8a', '#4f46e5', '#0f172a', '#7c3aed', '#2563eb', '#0891b2'];

    // Thêm các đường nhiễu nhẹ
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    for (let i = 0; i < code.length; i++) {
      const x = 15 + i * 18;
      const y = 22 + Math.random() * 6 - 3;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((Math.random() * 20 - 10) * (Math.PI / 180)); // Xoay nhẹ chữ

      ctx.fillStyle = colors[i % colors.length]; // Chọn màu theo danh sách
      ctx.fillText(code[i], 0, 0);

      ctx.restore();
    }
  }

  // Cập nhật khi trang load
  useEffect(() => {
    drawCaptcha(captcha);
  }, [captcha]);

  // Làm mới captcha
  const refreshCaptcha = () => {
    const newCaptcha = generateCaptcha();
    setCaptcha(newCaptcha);
    setUserInput('');
  };

  // Kiểm tra mã xác thực
  const handleVerifyCaptcha = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      alert('Vui lòng nhập email!');
      return;
    }
    if (!userInput) {
      alert('Vui lòng nhập mã xác thực!');
      return;
    }
    if (userInput.toUpperCase() === captcha) {
      alert('Xác thực thành công! Tiếp tục đặt lại mật khẩu.');
    } else {
      alert('Mã xác thực không đúng, vui lòng thử lại.');
      refreshCaptcha();
    }
  };

  return (
    <div>
      {/* Localized Form Header */}
      <div className='mb-8 text-center'>
        <h2 className='text-3xl font-extrabold tracking-tight text-slate-900'>Quên mật khẩu</h2>
        <p className='mt-2 text-sm text-slate-500'>Xác thực email để bắt đầu quy trình đặt lại mật khẩu.</p>
      </div>

      <form className='flex flex-col gap-5' onSubmit={handleVerifyCaptcha}>
        <div>
          <label className='block pb-1.5 text-xs font-bold uppercase tracking-wider text-slate-500'>
            Email đăng ký
          </label>
          <input
            type='email'
            className='h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 hover:bg-slate-50 focus:border-[#023e8a] focus:bg-white focus:ring-4 focus:ring-[#023e8a]/10'
            placeholder='Nhập email đã đăng ký của bạn'
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete='email'
          />
        </div>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto]'>
          <div className='flex-1'>
            <label className='block pb-1.5 text-xs font-bold uppercase tracking-wider text-slate-500'>
              Mã xác thực
            </label>
            <input
              type='text'
              className='h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 text-sm uppercase text-slate-900 outline-none transition-all duration-200 hover:border-slate-300 hover:bg-slate-50 focus:border-[#023e8a] focus:bg-white focus:ring-4 focus:ring-[#023e8a]/10'
              placeholder='Nhập mã ở bên'
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              required
            />
          </div>

          <div className='flex items-end justify-start sm:justify-end'>
            <div className='flex w-full items-center gap-2 sm:w-auto'>
              <canvas
                ref={canvasRef}
                width={120}
                height={44}
                className='h-11 w-[120px] overflow-hidden rounded-xl border border-slate-200 bg-slate-50'
              />
              <button
                type='button'
                title='Tạo mã xác thực khác'
                onClick={refreshCaptcha}
                className='flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 transition-all duration-150 hover:border-slate-300 hover:text-slate-600 focus:outline-none active:scale-[0.95]'
              >
                <RotateCw size={16} />
              </button>
            </div>
          </div>
        </div>

        <button
          type='submit'
          className='mt-2 h-11 w-full rounded-xl bg-[#023e8a] text-sm font-bold text-white shadow-sm transition-all duration-150 hover:bg-[#012e66] focus:outline-none focus:ring-4 focus:ring-[#023e8a]/20 active:scale-[0.985]'
        >
          Lấy lại mật khẩu
        </button>

        <div className='flex items-center justify-center pt-2 text-center'>
          <button
            type='button'
            onClick={() => toggleForm('login')}
            className='flex items-center justify-center gap-1 text-xs font-bold text-slate-500 transition-colors hover:text-slate-700'
          >
            <ChevronLeft className='h-4 w-4 stroke-[2.5]' />
            Quay lại đăng nhập
          </button>
        </div>
      </form>
    </div>
  );
}
